import React, { useCallback, useState } from 'react';
import {
  Button,
  FileButton,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconUpload } from '@tabler/icons-react';
import { useUploadPropertyImageMutation } from '@repo/services';
import { formatDateForFilename } from '@repo/utils';

/**
 * Props for the BlobPictureUpload component that handles single or batch Azure Blob image uploads for a property.
 */
export interface BlobPictureUploadProps {
  propertyId: string;
  onUploaded?: (successCount: number, failCount: number) => void;
  disabled?: boolean;
}

/**
 * Upload button + preview/rename modal for Azure Blob property images.
 * Single-file uploads open a preview modal; multi-file uploads go straight through.
 */
export function BlobPictureUpload({ propertyId, onUploaded, disabled }: BlobPictureUploadProps) {
  const [uploadImage, { isLoading: isUploading }] = useUploadPropertyImageMutation!();

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [imageType, setImageType] = useState('Image');
  const [imageSubType, setImageSubType] = useState('General');
  const [notes, setNotes] = useState('');
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [fileButtonKey, setFileButtonKey] = useState(() => Date.now());

  const resetUploadState = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setOriginalFile(null);
    setRenameValue('');
    setImageType('Image');
    setImageSubType('General');
    setNotes('');
    setUploadStatus(null);
    setUploadModalOpen(false);
    setFileButtonKey(Date.now());
  }, [previewUrl]);

  const handleFileSelect = async (files: File[]) => {
    if (!files.length) return;

    if (files.length === 1) {
      const file: File | undefined = files[0];
      if (!file) return;
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setOriginalFile(file);
      const baseName = file.name.replace(/\.[^.]+$/, '');
      setRenameValue(baseName.toLowerCase() === 'image' ? '' : baseName);
      setUploadModalOpen(true);
    } else {
      setUploadStatus('Uploading\u2026');
      let successCount = 0;
      let failCount = 0;
      for (const file of files) {
        try {
          await uploadImage({ propertyId, file, type: 'Image', subType: 'General' }).unwrap();
          successCount++;
        } catch {
          failCount++;
        }
      }
      setUploadStatus(
        `\u2705 ${successCount} uploaded${failCount > 0 ? `, \u274C ${failCount} failed` : ''}`,
      );
      onUploaded?.(successCount, failCount);
      setTimeout(() => { setUploadStatus(null); setFileButtonKey(Date.now()); }, 3000);
    }
  };

  const performSingleUpload = async () => {
    if (!originalFile || !propertyId) return;
    setUploadStatus('Uploading\u2026');

    const extMatch = originalFile.name.match(/\.[^.]+$/);
    const extension = extMatch ? extMatch[0] : '';
    let finalBase = (renameValue || originalFile.name.replace(/\.[^.]+$/, '')).trim();
    if (!finalBase || finalBase.toLowerCase() === 'image') {
      finalBase = formatDateForFilename();
    }
    const finalName = finalBase + extension.toLowerCase();
    const fileToUpload =
      finalName === originalFile.name
        ? originalFile
        : new File([originalFile], finalName, { type: originalFile.type });

    try {
      await uploadImage({
        propertyId,
        file: fileToUpload,
        type: imageType,
        subType: imageSubType,
        notes: notes || undefined,
      }).unwrap();
      setUploadStatus('\u2705 Upload successful');
      onUploaded?.(1, 0);
      setTimeout(resetUploadState, 1500);
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadStatus('\u274C Upload failed');
    }
  };

  return (
    <>
      <FileButton key={fileButtonKey} multiple onChange={handleFileSelect} accept="image/*,video/*">
        {(props) => (
          <Button
            size="xs"
            leftSection={isUploading ? <Loader size="xs" /> : <IconUpload size={14} />}
            loading={isUploading}
            variant="light"
            color="blue"
            radius="xl"
            disabled={disabled || !propertyId || isUploading}
            {...props}
          >
            Upload
          </Button>
        )}
      </FileButton>

      {uploadStatus && !uploadModalOpen && (
        <Text size="sm" c={uploadStatus.startsWith('\u274C') ? 'red' : 'green'}>
          {uploadStatus}
        </Text>
      )}

      <Modal
        opened={uploadModalOpen}
        onClose={() => !isUploading && resetUploadState()}
        title="Upload Image"
        centered
        size="md"
        overlayProps={{ opacity: 0.55, blur: 3 }}
      >
        <Stack gap="sm">
          {previewUrl && (
            originalFile?.type.startsWith('video/') ? (
              <video src={previewUrl} controls style={{ width: '100%', maxHeight: 320, objectFit: 'contain', borderRadius: 4, border: '1px solid #eee' }} />
            ) : (
              <img src={previewUrl} alt="preview" style={{ width: '100%', maxHeight: 320, objectFit: 'contain', borderRadius: 4, border: '1px solid #eee' }} />
            )
          )}
          <TextInput label="File name" placeholder="Enter a descriptive name" value={renameValue} onChange={(e) => setRenameValue(e.currentTarget.value)} description="Extension will be preserved" disabled={isUploading} />
          <TextInput label="Type" placeholder="Image" value={imageType} onChange={(e) => setImageType(e.currentTarget.value)} disabled={isUploading} />
          <TextInput label="SubType" placeholder="General" value={imageSubType} onChange={(e) => setImageSubType(e.currentTarget.value)} disabled={isUploading} />
          <TextInput label="Notes" placeholder="Optional notes" value={notes} onChange={(e) => setNotes(e.currentTarget.value)} disabled={isUploading} />
          {uploadStatus && (
            <Text size="sm" c={uploadStatus.startsWith('\u274C') ? 'red' : uploadStatus.startsWith('\u2705') ? 'green' : undefined}>
              {uploadStatus}
            </Text>
          )}
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={resetUploadState} disabled={isUploading}>Cancel</Button>
            <Button onClick={performSingleUpload} loading={isUploading} disabled={!originalFile}>Upload</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

